import React, { useState, useEffect } from 'react';
import { Lock, Download, Mail, Phone, Pencil, Send, Trash2, CheckCircle, LogIn, LogOut } from 'lucide-react';
import { api } from '../api';
import ImportCSV from '../components/ImportCSV';
import PhoneInput from '../components/PhoneInput';

const API_URL = 'https://qbdp-backend-production.up.railway.app/api';
const card = { background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: '20px 24px' };

const STATUT_LABELS = { reserve: 'Réservé', actif: 'Présent', checkout: 'Absent', annule: 'Annulé' };

// Statut calculé depuis les dates — prioritaire sur le statut DB
function getEffectiveStatut(c) {
  if (c.statut === 'annule') return 'annule';
  const today = new Date().toISOString().split('T')[0];
  if (c.date_arrivee && c.date_arrivee.slice(0, 10) > today) return 'reserve';
  if (c.date_depart  && c.date_depart.slice(0, 10)  < today && c.statut === 'actif') return 'checkout';
  return c.statut;
}
function StatusBadge({ statut }) {
  const map = {
    actif:    { bg: 'var(--green-light)', color: 'var(--green)' },
    checkout: { bg: '#fef2f2',            color: 'var(--red)'   },
    reserve:  { bg: 'var(--amber-light)', color: 'var(--amber)' },
    annule:   { bg: '#fef2f2',            color: 'var(--red)'   },
  };
  const s = map[statut] || map.actif;
  return <span style={{ background: s.bg, color: s.color, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{STATUT_LABELS[statut] || statut}</span>;
}

function toDateInput(val) {
  if (!val) return '';
  return val.slice(0, 10);
}

function splitNom(nom) {
  if (!nom) return { prenom: '', nom_famille: '' };
  const i = nom.indexOf(' ');
  if (i === -1) return { prenom: '', nom_famille: nom };
  return { nom_famille: nom.slice(0, i), prenom: nom.slice(i + 1) };
}

function F({ label, children }) {
  return <div><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>{label}</div>{children}</div>;
}

function Modal({ onClose, onSave, onDelete, initial, onGererAcces }) {
  const split = splitNom(initial?.nom);
  const [form, setForm] = useState(initial ? {
    ...initial,
    prenom:         split.prenom,
    nom_famille:    split.nom_famille,
    date_arrivee:   toDateInput(initial.date_arrivee),
    date_depart:    toDateInput(initial.date_depart),
    date_naissance: toDateInput(initial.date_naissance),
  } : {
    prenom: '', nom_famille: '', type: 'hotel', email: '', telephone: '',
    adresse: '', code_postal: '', ville: '', numero_chambre: '',
    date_arrivee: '', date_depart: '', date_naissance: '', statut: 'reserve',
    prix_nuit: '', consentement_email: false, consentement_sms: false, notes: '',
  });
  // Uppercase la ville au chargement initial (données existantes)
  useEffect(() => { if (form.ville) set('ville', form.ville.toUpperCase()); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', marginTop: 4, boxSizing: 'border-box' };
  const lbl = { fontSize: 11, fontWeight: 500, color: '#374151' };

  const [villeSuggestions, setVilleSuggestions] = useState([]);
  const [showVilleSuggestions, setShowVilleSuggestions] = useState(false);

  const fmtCp = (cp) => cp.length > 2 ? cp.slice(0, 2) + ' ' + cp.slice(2) : cp;

  const handleCodePostal = (e) => {
    const d = e.target.value.replace(/\D/g, '').slice(0, 5);
    set('code_postal', d.length > 2 ? d.slice(0, 2) + ' ' + d.slice(2) : d);
    if (d.length === 5) {
      fetch(`https://geo.api.gouv.fr/communes?codePostal=${d}&fields=nom&limit=5&boost=population`)
        .then(r => r.json())
        .then(data => { if (data.length > 0) set('ville', data[0].nom.toUpperCase()); })
        .catch(() => {});
    }
  };

  const handleVille = (e) => {
    const val = e.target.value.toUpperCase();
    set('ville', val);
    if (val.length >= 2) {
      fetch(`https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(val)}&fields=nom,codesPostaux&limit=6&boost=population`)
        .then(r => r.json())
        .then(data => {
          const sugg = data.map(c => ({ nom: c.nom.toUpperCase(), cp: c.codesPostaux[0] || '' }));
          setVilleSuggestions(sugg);
          setShowVilleSuggestions(sugg.length > 0);
          // Auto-remplit le code postal avec le premier résultat
          if (sugg.length > 0) set('code_postal', fmtCp(sugg[0].cp));
        })
        .catch(() => {});
    } else {
      setVilleSuggestions([]);
      setShowVilleSuggestions(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, padding: '20px 24px', width: 660, maxHeight: '95vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>{initial ? 'Modifier' : 'Ajouter'} un client / patient</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Prénom | Nom | Réf | Type | Statut */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10 }}>
            <F label="Nom"><input style={inp} placeholder="DUPONT" value={form.nom_famille} onChange={e => set('nom_famille', e.target.value)} /></F>
            <F label="Prénom"><input style={inp} placeholder="Olivier" value={form.prenom} onChange={e => set('prenom', e.target.value)} /></F>
            <F label="N° Référence"><input style={inp} placeholder="204…" value={form.numero_chambre} onChange={e => set('numero_chambre', e.target.value)} /></F>
            <F label="Type">
              <select style={inp} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="hotel">Hôtel</option>
                <option value="hopital">Hôpital</option>
                <option value="clinique">Clinique</option>
                <option value="residence">Résidence</option>
                <option value="autre">Autre</option>
              </select>
            </F>
            <F label="Statut">
              <select style={inp} value={form.statut} onChange={e => set('statut', e.target.value)}>
                <option value="reserve">Réservé</option>
                <option value="actif">Présent</option>
                <option value="checkout">Absent</option>
                <option value="annule">Annulé</option>
              </select>
            </F>
          </div>

          {/* Dates + Prix */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            <F label="Date d'arrivée"><input style={inp} type="date" value={form.date_arrivee} onChange={e => {
              const v = e.target.value;
              set('date_arrivee', v);
              if (v && !initial) set('statut', 'reserve');
            }} /></F>
            <F label="Date de départ"><input style={inp} type="date" value={form.date_depart} onChange={e => set('date_depart', e.target.value)} /></F>
            <F label="Prix / nuit (€)"><input style={inp} type="number" min="0" step="0.01" placeholder="120" value={form.prix_nuit || ''} onChange={e => set('prix_nuit', e.target.value)} /></F>
            <F label="Date de naissance"><input style={inp} type="date" value={form.date_naissance || ''} onChange={e => set('date_naissance', e.target.value)} /></F>
          </div>

          {/* Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <F label="Email"><input style={inp} type="email" placeholder="client@email.fr" value={form.email} onChange={e => set('email', e.target.value)} /></F>
            <F label="Téléphone"><PhoneInput value={form.telephone} onChange={v => set('telephone', v)} /></F>
          </div>

          {/* Adresse */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 100px 1fr', gap: 10 }}>
            <F label="Adresse"><input style={inp} placeholder="12 rue de la Paix" value={form.adresse} onChange={e => set('adresse', e.target.value)} /></F>
            <F label="Code postal"><input style={inp} placeholder="75001" value={form.code_postal} onChange={handleCodePostal} /></F>
            <F label="Ville">
              <div style={{ position: 'relative' }}>
                <input style={inp} placeholder="Paris" value={form.ville}
                  onChange={handleVille}
                  onBlur={() => setTimeout(() => setShowVilleSuggestions(false), 150)} />
                {showVilleSuggestions && villeSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 300, overflow: 'hidden', marginTop: 2 }}>
                    {villeSuggestions.map((s, i) => (
                      <div key={i}
                        onMouseDown={() => {
                          set('ville', s.nom);
                          set('code_postal', fmtCp(s.cp));
                          setShowVilleSuggestions(false);
                        }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i < villeSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <span>{s.nom}</span>
                        <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 8 }}>{s.cp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </F>
          </div>

          {/* Notes */}
          <F label="Notes"><textarea style={{ ...inp, resize: 'none', height: 52 }} placeholder="Informations complémentaires..." value={form.notes} onChange={e => set('notes', e.target.value)} /></F>

          {/* Consentements */}
          <div style={{ display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.consentement_email} onChange={e => set('consentement_email', e.target.checked)} />
              Consentement email
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.consentement_sms} onChange={e => set('consentement_sms', e.target.checked)} />
              Consentement SMS
            </label>
          </div>
        </div>
        {initial?.id && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button onClick={() => { onClose(); onGererAcces(initial); }}
              style={{ width: '100%', background: '#f0f5ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px', fontSize: 13, color: '#1a56db', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Lock size={14} /> Gérer les accès & zones
            </button>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          {initial?.id && onDelete ? (
            <button onClick={() => onDelete(initial.id)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Trash2 size={14} /> Supprimer
            </button>
          ) : <div />}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'white', cursor: 'pointer' }}>Annuler</button>
            <button onClick={() => onSave({ ...form, nom: [form.nom_famille, form.prenom].filter(Boolean).join(' ') })} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--blue)', color: 'white', fontWeight: 500, cursor: 'pointer' }}>Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalConfirm({ title, clientInfo, body, confirmLabel, confirmColor = '#1a56db', icon: Icon = Send, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,40,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onCancel}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 460, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: `linear-gradient(135deg, ${confirmColor} 0%, ${confirmColor}cc 100%)`, padding: '32px 28px 36px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Icon size={30} color="white" />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: -0.3 }}>{title}</div>
        </div>
        <div style={{ padding: '24px 28px 28px' }}>
          {clientInfo && (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontWeight: 600, fontSize: 13, color: '#374151', textAlign: 'center' }}>
              {clientInfo}
            </div>
          )}
          <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: 24, textAlign: 'center' }}>{body}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onCancel} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Annuler</button>
            <button onClick={onConfirm} style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: confirmColor, color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>{confirmLabel || 'Confirmer'}</button>
          </div>
          <div style={{ textAlign: 'center', marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Lock size={10} color="#d1d5db" />
            <span style={{ fontSize: 10, color: '#d1d5db', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>QBDP · Quality Badge & Digital Protocol</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalEnvoi({ selected, defaultType, onClose, onSent }) {
  const [type, setType]       = useState(defaultType || 'email');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  const recipients = selected.map(s => ({ nom: s.nom, email: s.email, telephone: s.telephone }));
  const emailCount = recipients.filter(r => r.email).length;
  const smsCount   = recipients.filter(r => r.telephone).length;

  const openSmsApp = () => {
    const phones = recipients.filter(r => r.telephone).map(r => r.telephone).join(',');
    const body   = encodeURIComponent(message);
    window.open(`sms:${phones}${/iPhone|iPad|Mac/i.test(navigator.userAgent) ? '&' : '?'}body=${body}`);
    if (onSent) onSent();
    setResult({ smsDone: true, count: recipients.filter(r => r.telephone).length });
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    // SMS uniquement → ouvre l'app SMS native (forfait téléphonique)
    if (type === 'sms') { openSmsApp(); return; }

    setSending(true);
    try {
      const r = await fetch(`${API_URL}/campaigns/send-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients, subject, message, type: 'email' }),
      }).then(r => r.json());

      // Si "les deux" → ouvre aussi l'app SMS après l'email
      if (type === 'both' && smsCount > 0) {
        const phones = recipients.filter(r => r.telephone).map(r => r.telephone).join(',');
        const body   = encodeURIComponent(message);
        window.open(`sms:${phones}${/iPhone|iPad|Mac/i.test(navigator.userAgent) ? '&' : '?'}body=${body}`);
      }

      setResult(r);
      if (onSent) onSent();
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  const inp = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a2e' }}>Envoyer un message</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>

        {result ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {result.error
              ? <div style={{ color: '#dc2626', fontWeight: 600 }}>Erreur : {result.error}</div>
              : <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><CheckCircle size={36} color="#15803d" /></div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#15803d' }}>
                    {result.smsDone ? `App SMS ouverte — ${result.count} numéro(s) pré-rempli(s)` : 'Email envoyé !'}
                  </div>
                  {result.emailSent > 0 && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{result.emailSent} email{result.emailSent > 1 ? 's' : ''} envoyé{result.emailSent > 1 ? 's' : ''}</p>}
                  {result.emailFailed > 0 && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{result.emailFailed} échec — depuis: {result.fromEmail}</p>}
                  {result.errors?.map((e, i) => <p key={i} style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{e}</p>)}
                  {type === 'both' && smsCount > 0 && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>App SMS ouverte avec {smsCount} numéro(s)</p>}
                  <button onClick={onClose} style={{ marginTop: 20, background: '#1e3a8a', color: 'white', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}>Fermer</button>
                </>}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[
                { v: 'email', label: `Email (${emailCount})` },
                { v: 'sms',   label: `SMS (${smsCount})`     },
                { v: 'both',  label: `Les deux`               },
              ].map(opt => (
                <button key={opt.v} onClick={() => setType(opt.v)}
                  style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: `2px solid ${type === opt.v ? '#1e3a8a' : '#e5e7eb'}`,
                    background: type === opt.v ? '#eff6ff' : 'white', color: type === opt.v ? '#1e3a8a' : '#6b7280',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {type === 'sms' && (
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#1e3a8a' }}>
                L'app SMS de votre téléphone s'ouvrira avec les numéros et le message pré-remplis — l'envoi utilise votre forfait.
              </div>
            )}

            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#374151' }}>
              {selected.length} destinataire{selected.length > 1 ? 's' : ''}
              {' — '}{type === 'email' ? `${emailCount} email(s)` : type === 'sms' ? `${smsCount} SMS` : `${emailCount} email(s) + ${smsCount} SMS`}
            </div>

            {(type === 'email' || type === 'both') && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Objet</label>
                <input style={inp} placeholder="Objet de l'email" value={subject} onChange={e => setSubject(e.target.value)} />
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message</label>
              <textarea style={{ ...inp, minHeight: 120, resize: 'vertical' }} placeholder="Votre message..." value={message} onChange={e => setMessage(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Annuler</button>
              <button onClick={handleSend} disabled={sending || !message.trim()}
                style={{ flex: 2, padding: '11px 0', borderRadius: 10, border: 'none', background: '#1e3a8a', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 13, opacity: (!message.trim() || sending) ? 0.6 : 1 }}>
                {sending ? 'Envoi...' : type === 'sms' ? 'Ouvrir SMS' : 'Envoyer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SelectionBar({ selected, onClear, onSendEmail, onSendSms, onSendBoth }) {
  const emailCount = selected.filter(s => s.email).length;
  const smsCount   = selected.filter(s => s.telephone).length;
  if (!selected.length) return null;
  const btn = (onClick, bg, label) => (
    <button onClick={onClick} style={{ background: bg, border: 'none', borderRadius: 8, padding: '6px 14px', color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
      {label}
    </button>
  );
  return (
    <div style={{ background: '#1a1a2e', color: 'white', borderRadius: 12, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, marginRight: 4 }}>
        {selected.length} sélectionné{selected.length > 1 ? 's' : ''}
        {emailCount > 0 && ` · ${emailCount} email`}
        {smsCount > 0   && ` · ${smsCount} SMS`}
      </span>
      {emailCount > 0 && btn(onSendEmail, '#1e3a8a', 'Envoyer mail')}
      {smsCount   > 0 && btn(onSendSms,   '#5b8dd9', 'Envoyer SMS')}
      {emailCount > 0 && smsCount > 0 && btn(onSendBoth, '#7c3aed', 'Email + SMS')}
      <button onClick={onClear} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '6px 12px', color: 'white', fontSize: 12, cursor: 'pointer' }}>
        Effacer
      </button>
    </div>
  );
}

export default function Clients({ onGererAcces, clients: clientsFromApp, setClients: setClientsFromApp }) {
  const [localClients, setLocalClients] = useState([]);
  const clients = clientsFromApp !== undefined ? clientsFromApp : localClients;
  const setClients = setClientsFromApp !== undefined ? setClientsFromApp : setLocalClients;
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(null);
  const [search, setSearch]             = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [selected, setSelected]         = useState([]);
  const [sortCol, setSortCol]           = useState(() => sessionStorage.getItem('clients_sortCol') || 'created_at');
  const [sortDir, setSortDir]           = useState(() => sessionStorage.getItem('clients_sortDir') || 'desc');
  const [toastMsg, setToastMsg]         = useState('');
  const [showImport, setShowImport]     = useState(false);
  const [modalEnvoi, setModalEnvoi]     = useState(null); // null | 'email' | 'sms' | 'both'
  const [confirmModal, setConfirmModal] = useState(null);

  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(''), 3000); };

  const load = async () => {
    try {
      const data = await api.getClients();
      setClients(Array.isArray(data) ? data : []);
    } catch { setClients([]); }
    setLoading(false);
  };

  const exportCSV = () => {
    const token = sessionStorage.getItem('qbdp_token');
    fetch('https://qbdp-backend-production.up.railway.app/api/export/clients', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `clients_${new Date().toISOString().slice(0,10)}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    try {
      if (modal?.id) await api.updateClient(modal.id, form);
      else await api.createClient(form);
      load(); setModal(null);
    } catch (e) { alert(e.message); }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      title: 'Supprimer ce client',
      icon: Trash2, confirmColor: '#dc2626',
      clientInfo: null,
      body: 'Cette action est irréversible. Le client et toutes ses données seront définitivement supprimés.',
      confirmLabel: 'Supprimer définitivement',
      confirmColor: '#dc2626',
      onConfirm: async () => {
        setConfirmModal(null);
        setModal(null);
        await api.deleteClient(id);
        load();
      },
    });
  };

  const handleQuickStatut = (client, statut) => {
    const nom = client.nom || 'ce client';
    const ref = client.numero_chambre ? ` — Réf. ${client.numero_chambre}` : '';
    setConfirmModal({
      title: statut === 'actif' ? 'ARRIVÉE — Activation des accès' : 'DÉPART — Désactivation des accès',
      icon: statut === 'actif' ? LogIn : LogOut,
      clientInfo: `${nom}${ref}`,
      body: statut === 'actif'
        ? "En confirmant, vous activez immédiatement les droits d'accès de ce client.\n\nEn tant qu'administrateur, vous confirmez avoir vérifié l'identité du client et être responsable de cette activation. QBDP ne peut être tenu responsable des accès accordés suite à cette confirmation."
        : "En confirmant, vous désactivez immédiatement les droits d'accès de ce client. Il ne pourra plus ouvrir les portes associées à son séjour.\n\nEn tant qu'administrateur, vous êtes responsable de cette désactivation. QBDP ne peut être tenu responsable de l'accès résiduel éventuel lié à un appareil non synchronisé.",
      confirmLabel: statut === 'actif' ? "Confirmer l'arrivée" : 'Confirmer le départ',
      confirmColor: statut === 'actif' ? '#16a34a' : '#dc2626',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.updateClientStatut(client.id, statut);
          load();
        } catch (e) { alert(e.message); }
      },
    });
  };

  const handleInvite = (client) => {
    if (!client.email) { alert('Ce client n\'a pas d\'email'); return; }
    setConfirmModal({
      title: 'Envoyer une invitation',
      icon: Send, confirmColor: '#1a56db',
      clientInfo: `${client.nom} — ${client.email}`,
      body: `Un email d'invitation sera envoyé à ${client.email} avec un lien pour télécharger l'application QBDP et accéder à son séjour.`,
      confirmLabel: 'Envoyer l\'invitation',
      confirmColor: '#1a56db',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const token = sessionStorage.getItem('qbdp_token');
          const r = await fetch(`${API_URL}/invitations/envoyer-client`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ client_id: client.id })
          });
          const d = await r.json();
          if (d.success) showToast(`ok:Invitation envoyée à ${client.email}`);
          else showToast('err:' + (d.error || 'Erreur'));
        } catch { showToast('err:Erreur réseau'); }
      },
    });
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      const newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
      sessionStorage.setItem('clients_sortDir', newDir);
    } else {
      setSortCol(col);
      setSortDir('asc');
      sessionStorage.setItem('clients_sortCol', col);
      sessionStorage.setItem('clients_sortDir', 'asc');
    }
  };

  const toggleSelect = (client) => {
    setSelected(prev =>
      prev.find(s => s.id === client.id)
        ? prev.filter(s => s.id !== client.id)
        : [...prev, client]
    );
  };

  const filtered = clients
    .filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.nom?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.numero_chambre?.toLowerCase().includes(q);
      const matchStatut = !filtreStatut || c.statut === filtreStatut;
      return matchSearch && matchStatut;
    })
    .sort((a, b) => {
      let va = a[sortCol] || ''; let vb = b[sortCol] || '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });


  const thStyle = (col) => ({
    padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: sortCol === col ? 'var(--blue)' : 'var(--gray)',
    textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  });

  const today = new Date();

  return (
    <div style={{ padding: 28 }}>

      {toastMsg && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 2000, padding: '12px 20px', borderRadius: 12,
          background: toastMsg.startsWith('ok:') ? '#f0fdf4' : '#fef2f2',
          color: toastMsg.startsWith('ok:') ? '#16a34a' : '#dc2626',
          border: `1px solid ${toastMsg.startsWith('ok:') ? '#bbf7d0' : '#fecaca'}`,
          fontWeight: 600, fontSize: 13, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {toastMsg.startsWith('ok:') ? <CheckCircle size={16} /> : null}
          {toastMsg.replace(/^(ok:|err:)/, '')}
        </div>
      )}

      {modal !== null && (
        <Modal onClose={() => setModal(null)} onSave={handleSave} onDelete={handleDelete}
          initial={modal === 'new' ? null : modal} onGererAcces={onGererAcces} />
      )}

      {showImport && (
        <ImportCSV type="clients" onClose={() => setShowImport(false)} onSuccess={() => { load(); setShowImport(false); }} />
      )}

      {modalEnvoi && (
        <ModalEnvoi selected={selected} defaultType={modalEnvoi} onClose={() => setModalEnvoi(null)}
          onSent={() => showToast('ok:Message envoyé !')} />
      )}

      {confirmModal && (
        <ModalConfirm
          title={confirmModal.title}
          clientInfo={confirmModal.clientInfo}
          body={confirmModal.body}
          confirmLabel={confirmModal.confirmLabel}
          confirmColor={confirmModal.confirmColor}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>Clients / Patients</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>{clients.length} au total</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowImport(true)}
            style={{ background: 'white', color: '#1a56db', border: '2px solid #bfdbfe', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 170 }}>
            <Download size={14} /> Importer CSV
          </button>
          <button onClick={exportCSV}
            style={{ background: 'white', color: '#16a34a', border: '2px solid #bbf7d0', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 170 }}>
            <Download size={14} /> Exporter CSV
          </button>
          <button onClick={() => setModal('new')}
            style={{ background: 'var(--blue)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 170, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            + Ajouter un client
          </button>
        </div>
      </div>

      <SelectionBar
        selected={selected}
        onClear={() => setSelected([])}
        onSendEmail={() => setModalEnvoi('email')}
        onSendSms={() => setModalEnvoi('sms')}
        onSendBoth={() => setModalEnvoi('both')}
      />

      <div style={{ ...card, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', padding: '14px 20px' }}>
        <input placeholder="Rechercher nom, email, numéro..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }} />
        <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: 'white' }}>
          <option value="">Tous les statuts</option>
          <option value="reserve">Réservé</option>
          <option value="actif">Présent</option>
          <option value="checkout">Absent</option>
          <option value="annule">Annulé</option>
        </select>
        {selected.length > 0 && <span style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500 }}>{selected.length} sélectionné(s)</span>}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray)' }}>Chargement...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray)' }}>
            {search || filtreStatut ? 'Aucun résultat' : 'Aucun client — cliquez sur + Ajouter'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: '#fafafa' }}>
                <th style={{ width: 36, padding: '10px 14px' }}></th>
                <th style={thStyle('nom')} onClick={() => handleSort('nom')}>Nom {sortCol==='nom'?(sortDir==='asc'?'↑':'↓'):''}</th>
                <th style={thStyle('numero_chambre')} onClick={() => handleSort('numero_chambre')}>Numéro {sortCol==='numero_chambre'?(sortDir==='asc'?'↑':'↓'):''}</th>
                <th style={thStyle('statut')} onClick={() => handleSort('statut')}>Statut {sortCol==='statut'?(sortDir==='asc'?'↑':'↓'):''}</th>
                <th style={thStyle('date_arrivee')} onClick={() => handleSort('date_arrivee')}>Séjour {sortCol==='date_arrivee'?(sortDir==='asc'?'↑':'↓'):''}</th>
                <th style={thStyle('date_naissance')} onClick={() => handleSort('date_naissance')}>Anniversaire {sortCol==='date_naissance'?(sortDir==='asc'?'↑':'↓'):''}</th>
                <th style={thStyle('email')} onClick={() => handleSort('email')}>Contact {sortCol==='email'?(sortDir==='asc'?'↑':'↓'):''}</th>
                <th style={{ padding: '10px 14px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => {
                const isSel = selected.find(s => s.id === c.id);
                const isToday = c.date_naissance && (() => {
                  const dn = new Date(c.date_naissance);
                  return dn.getMonth()+1 === today.getMonth()+1 && dn.getDate() === today.getDate();
                })();
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border)', background: isSel ? '#eff6ff' : isToday ? '#fff9f0' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <input type="checkbox" checked={!!isSel} onChange={() => toggleSelect(c)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                          {c.initiales || c.nom?.slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{c.nom}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray)' }}>{c.type}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 13 }}>{c.numero_chambre || '—'}</td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge statut={getEffectiveStatut(c)} /></td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--gray)' }}>
                      {c.date_arrivee ? new Date(c.date_arrivee).toLocaleDateString('fr-FR') : '—'}
                      {c.date_depart ? ` → ${new Date(c.date_depart).toLocaleDateString('fr-FR')}` : ''}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: isToday ? '#d97706' : 'var(--gray)' }}>
                      {c.date_naissance ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isToday && '🎂'}
                          {new Date(c.date_naissance).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {c.email && <span style={{ color: c.consentement_email ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} />{c.email}</span>}
                        {c.telephone && <span style={{ color: c.consentement_sms ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11} />{c.telephone}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setModal(c)} title="Modifier"
                          style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={13} color="#374151" /></button>
                        <button onClick={() => onGererAcces(c)} title="Gérer les accès"
                          style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Lock size={13} color="#1a56db" /></button>
                        <button onClick={() => handleInvite(c)} title="Envoyer invitation"
                          style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Send size={13} color="#1a56db" /></button>
                        {getEffectiveStatut(c) === 'actif' ? (
                          <button onClick={() => handleQuickStatut(c, 'checkout')} title="Départ"
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fee2e2', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <LogOut size={13} color="#dc2626" />
                            <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Départ</span>
                          </button>
                        ) : (
                          <button onClick={() => handleQuickStatut(c, 'actif')} title="Arrivée"
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #bbf7d0', background: '#dcfce7', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <LogIn size={13} color="#16a34a" />
                            <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>Arrivée</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
